<template>
  <div class="main">
    <TableComponent v-if="reloadFlag" />

    <!-- Modal Section -->
    <b-modal
      v-model="modalOpen"
      id="modal-lg"
      title="Select Delivery Items"
      centered
      size="xl"
      style="width: auto"
    >
      <b-col
        cols="12"
        class="d-flex justify-content-end mb-2"
        style="position: relative; z-index: 1"
      >
        <b-button variant="outline-danger" size="sm" @click="clearFilters">
          Clear Filters
        </b-button>
      </b-col>
      <div class="d-flex justify-content-center mb-1">
        <b-spinner
          variant="success"
          label="Spinning"
          block
          v-if="loadingnotes"
          style="width: 3rem; height: 3rem"
        ></b-spinner>
      </div>
      <b-table
        striped
        hover
        responsive="lg"
        v-if="!loadingnotes"
        :items="filteredDeliveryItems"
        :fields="deliveryItemFieldsModal"
      >
        <!-- Header with Filters -->
        <template #head(invoiceNo)="scope">
          <div class="d-flex flex-column">
            <span>Inv #</span>
            <b-form-input
              v-model="filters.invoiceNo"
              placeholder="Search Invoice No"
              size="sm"
              @input="currentPage = 1"
            />
          </div>
        </template>

        <template #head(customer)="scope">
          <div class="d-flex flex-column">
            <span>Customer</span>
            <b-form-input
              v-model="filters.customer"
              placeholder="Search Customer"
              size="sm"
              @input="currentPage = 1"
            />
          </div>
        </template>

        <template #head(itemId)="scope">
          <div class="d-flex flex-column">
            <span>Product Name</span>
            <b-form-input
              v-model="filters.itemId"
              placeholder="Search Product"
              size="sm"
              @input="currentPage = 1"
            />
          </div>
        </template>

        <template #head(note)="scope">
          <div class="d-flex flex-column">
            <span>Note</span>
            <b-form-input
              v-model="filters.note"
              placeholder="Search Note"
              size="sm"
              @input="currentPage = 1"
            />
          </div>
        </template>

        <template #head(quantity)="scope">
          <div class="d-flex flex-column">
            <span>Quantity</span>
            <b-form-input
              v-model="filters.quantity"
              placeholder="Search Quantity"
              size="sm"
              @input="currentPage = 1"
            />
          </div>
        </template>

        <template #head(address)="scope">
          <div class="d-flex flex-column">
            <span>Address</span>
            <b-form-input
              v-model="filters.address"
              placeholder="Search Address"
              size="sm"
              @input="currentPage = 1"
            />
          </div>
        </template>

        <template #head(status)="scope">
          <div class="d-flex flex-column">
            <span>Status</span>
            <b-form-select
              v-model="filters.status"
              :options="statusOptions"
              size="sm"
              @change="currentPage = 1"
            ></b-form-select>
          </div>
        </template>

        <!-- Cell templates for specific fields -->
        <template #cell(invoiceNo)="data">
          {{ data.item.invoiceNo }}
        </template>
        <template #cell(customer)="data">
          {{ data.item.customer }}
        </template>
        <template #cell(address)="data">
          {{ data.item.address }}
        </template>

        <template #cell(itemId)="data">
          {{ getProductName(data.item.itemId) }}
        </template>

        <template #cell(quantity)="data">
          <span class="font-weight-bold">{{ data.item.quantity }}</span>
        </template>

        <template #cell(check)="row">
          <b-form-checkbox
            v-model="row.item.selected"
            @change="logSelectedRow(row.item)"
          />
        </template>
      </b-table>
      <template #modal-footer>
        <b-button @click="saveChanges" variant="primary">Save Changes</b-button>
      </template>
    </b-modal>

    <!-- Delivery Items Section -->
    <b-card class="mt-3">
      <b-card-header>
        <div class="d-flex justify-content-between align-items-center m-1">
          <div class="mr-5">Selected Delivery Item Details</div>
          <b-button @click="toggleModal" variant="primary">Select Delivery Items</b-button>
        </div>
      </b-card-header>
      <b-card-body>
        <!-- Table for delivery items, showing selected items -->
        <b-table
          striped
          hover
          responsive
          :items="selectedDataItems"
          :fields="selectedItemFields"
        >
          <template #cell(invoiceNo)="data">
            {{ data.item.invoice.invoiceNo }}
          </template>

          <template #cell(customer)="data">
            {{
              data.item.invoice.leadClient &&
              data.item.invoice.leadClient.type == 1
                ? data.item.invoice.leadClient.companyName
                : (
                    (data.item.invoice.leadClient.surname || "") +
                    " " +
                    (data.item.invoice.leadClient.otherName || "")
                  ).trim()
            }}
          </template>
          <template #cell(address)="data">
            {{
              (data.item.invoice.leadClient.addressLine1 || "") +
              " " +
              (data.item.invoice.leadClient.addressLine2 || "")
            }}
          </template>
          <template #cell(itemId)="data">
            {{ getProductName(data.item.itemId) }}
          </template>

          <!-- <template #cell(check)="row">
            <b-form-checkbox v-model="row.item.selected" />
            {{ row.item.selected }}
          </template> -->

          <template #cell(status)="row">
            <b-badge :variant="getStatusColor(row.item.status)">{{
              row.item.status
            }}</b-badge>
          </template>
        </b-table>
      </b-card-body>
    </b-card>

    <b-card>
      <b-card-body>
        <div>
          <validation-observer
            ref="AddDeliveryForm"
            @ok.prevent="SubmitDelivery"
          >
            <b-form @submit.prevent="SubmitDelivery">
              <b-form-group label="Vehicle *" label-for="vehicle">
                <validation-provider
                  v-slot="{ errors }"
                  name="vehicleId"
                  vid="vehicleId"
                  rules="required"
                >
                  <b-form-select
                    id="vehicleId"
                    v-model="delivery.vehicleId"
                    :options="vehicleOptions"
                    value-field="vehicleId"
                    text-field="make"
                    :state="errors.length > 0 ? false : null"
                    required
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
              <b-form-group label="Driver *" label-for="driver">
                <validation-provider
                  v-slot="{ errors }"
                  name="driverId"
                  vid="driverId"
                  rules="required"
                >
                  <b-form-select
                    id="driverId"
                    v-model="delivery.driverId"
                    :options="driverOptions"
                    required
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
              <b-form-group
                label="Pickup Location *"
                label-for="pickUpLocation"
              >
                <validation-provider
                  v-slot="{ errors }"
                  name="pickUpLocation"
                  vid="pickUpLocation"
                  rules="required"
                >
                  <b-form-select
                    id="pickUpLocation"
                    v-model="delivery.pickUpLocation"
                    :options="locationOptions"
                    required
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
              <b-form-group
                label="Dropoff Location *"
                label-for="dropOffLocation"
              >
                <validation-provider
                  v-slot="{ errors }"
                  name="dropOffLocation"
                  vid="dropOffLocation"
                  rules="required"
                >
                  <b-form-select
                    id="dropOffLocation"
                    v-model="delivery.dropOffLocation"
                    :options="locationOptions"
                    required
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <b-form-group label="Delivery Date *" label-for="deliveryDate">
                <validation-provider
                  v-slot="{ errors }"
                  name="deliveryDate"
                  vid="deliveryDate"
                  rules="required"
                >
                  <b-form-input
                    id="deliveryDate"
                    v-model="delivery.deliveryDate"
                    type="date"
                    :min="currentDate"
                    required
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
              <b-form-group
                label="Delivery Status *"
                label-for="deliveryStatus"
              >
                <validation-provider
                  v-slot="{ errors }"
                  name="deliveryStatus"
                  vid="deliveryStatus"
                  rules="required"
                >
                  <b-form-select
                    id="deliveryStatus"
                    v-model="delivery.deliveryStatus"
                    :options="deliveryStatusOptions"
                    required
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
              <b-form-group label="Notes *" label-for="notes">
                <validation-provider
                  v-slot="{ errors }"
                  name="notes"
                  vid="notes"
                  rules="required"
                >
                  <b-form-textarea id="notes" v-model="delivery.notes" />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <CustomFieldsDetails
                :AdditionalFields="delivery.additionalFields"
                :ModuleReference="'Delivery'"
                @onchange="additionalFieldChanges"
              ></CustomFieldsDetails>
              <!-- <b-button type="submit" variant="primary">Save Changes</b-button> -->
              <button
                type="button"
                class="btn btn-primary"
                block
                @click="SubmitDelivery()"
              >
                <b-spinner small v-if="loading" class="mx-2" />Save Changes
              </button>
            </b-form>
          </validation-observer>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormTextarea,
  BButton,
  BTable,
  BCardBody,
  BCardHeader,
  BModal,
  BFormCheckbox,
  BBadge,
  BSpinner,
} from "bootstrap-vue";
import { mapActions, mapGetters } from "vuex";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { extend } from "vee-validate";
// import { required } from 'vee-validate/dist/rules';
import { required } from "@validations";
import CustomFieldsDetails from "@/views/component/CustomFieldsDetails.vue";

extend("required", {
  ...required,
  message: "This field is required",
});

extend("dateNotBeforeToday", {
  validate(value) {
    const selectedDate = new Date(value);
    const today = new Date();
    // Reset time components to compare only dates
    selectedDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return selectedDate >= today;
  },
  message: "The delivery date cannot be before today",
});

export default {
  components: {
    BCard,
    BForm,
    BFormGroup,
    BFormInput,
    BCardBody,
    BFormSelect,
    BFormTextarea,
    BButton,
    BTable,
    BModal,
    BFormCheckbox,
    BBadge,
    BCardHeader,
    BSpinner,
    ValidationProvider,
    ValidationObserver,
    CustomFieldsDetails,
  },
  data() {
    return {
      modalOpen: false,
      reloadFlag: true,
      loading: true,
      delivery: {
        vehicleId: null,
        driverId: null,
        pickUpLocation: "",
        dropOffLocation: "",
        deliveryDate: "",
        deliveryStatus: "",
        notes: "",
        additionalFields: [],
        reloadFlag: true,
      },
      created() {
        this.$on("reloadTableData", this.reloadTable);
      },
      items: [],
      filters: {
        invoiceNo: "",
        customer: "",
        itemId: "",
        note: "",
        address: "",
        quantity: "",
        status: null,
      },
      statusOptions: [
        { value: null, text: "All" },
        { value: "PENDING", text: "Pending" },
        { value: "INTRANSIT", text: "In Transit" },
        { value: "DELIVERED", text: "Delivered" },
        { value: "SCHEDULED", text: "Scheduled" },
      ],
      deliveryItemFieldsModal: [
        { key: "invoiceNo", label: "Inv #", sortable: true },
        { key: "customer", label: "Customer", sortable: true },
        { key: "itemId", label: "Product Name", sortable: true },
        { key: "note", label: "Note", sortable: true },
        { key: "address", label: "Address", sortable: true },
        { key: "quantity", label: "Quantity", sortable: true },
        // { key: 'status', label: 'Status' },
        { key: "check", label: "Select" },
      ],
      selectedItemFields: [
        { key: "invoiceNo", label: "Inv #" },
        { key: "customer", label: "Customer" },
        { key: "itemId", label: "Product Name" },
        { key: "note", label: "Note" },
        { key: "address", label: "Address" },
        { key: "quantity", label: "Quantity" },
        { key: "status", label: "Status", sortable: true },
      ],
      // deliveryStatusOptions: ["Pending", "In Transit", "Completed"],
      deliveryStatusOptions: [
        { value: "PENDING", text: "Pending" },
        { value: "INTRANSIT", text: "In Transit" },
        { value: "DELIVERED", text: "Delivered" },
        { value: "CANCELLED", text: "Cancelled" },
        { value: "RETURNED", text: "Returned" },
      ],
      locationOptions: [
        "16eme Mille",
        "Agalega",
        "Albion",
        "Amitie Gokhoola",
        "Arsenal",
        "Baie Du Cap",
        "Bambous",
        "Bambous Virieux",
        "Bananes",
        "Beau Bassin",
        "Beau Vallon",
        "Bel Air",
        "Bel Ombre",
        "Benares",
        "Black River",
        "Bois Cheri",
        "Bois Des Amourettes",
        "Bon Accueil",
        "Bramsthan",
        "Brisee Verdiere",
        "Britannia",
        "Calebasses",
        "Camp Carol",
        "Camp De Masque",
        "Camp De Masque Pave",
        "Camp Diable",
        "Camp Ithier",
        "Cap Malheureux",
        "Cascavelle",
        "Case Noyale",
        "Central Flacq",
        "Chamarel",
        "Chamouny",
        "Chemin Grenier",
        "Clemencia",
        "Cluny",
        "Congomah",
        "Cottage",
        "Creve Coeur",
        "Curepipe",
        "D'Epinay",
        "Dagotiere",
        "Dubreuil",
        "Eau Coulee",
        "Ebene",
        "Ecroignard",
        "Flic en Flac",
        "Fond du Sac",
        "FOREST - SIDE",
        "G.R.S.E",
        "Goodlands",
        "Grand Bay",
        "Grand Bel Air",
        "Grand Bois",
        "Grand Gaube",
        "Grand Sable",
        "Grande Retraite",
        "Gros Cailloux",
        "L'Avenir",
        "L'Escalier",
        "L'Esperance",
        "L'Esperance Trebuchet",
        "La Flora",
        "La Gaulette",
        "La Laura / Malenga",
        "Lalmatie",
        "Laventure",
        "Le Hochet",
        "Le Morne",
        "Long Mountain",
        "Mahebourg",
        "Mapou",
        "Mare D'Albert",
        "Mare La Chaux",
        "Mare Tabac",
        "Medine Camp de Masque",
        "Melrose",
        "Midlands",
        "Moka",
        "Montagne Blanche",
        "Morc St Andre",
        "New Grove",
        "Notre Dame",
        "Nouvelle Decouverte",
        "Nouvelle France",
        "Old Grand Port",
        "Olivia",
        "Pamplemousses",
        "Petit Bel Air",
        "Petit Raffray",
        "Petite Riviere",
        "Phoenix",
        "Piton",
        "Plaine des Papayes",
        "Plaine Magnien",
        "Pointe aux Piments",
        "Pointe aux Sables",
        "PORT LOUIS",
        "Poste de Flacq",
        "Poudre D'Or Hamlet",
        "Poudre D'Or Village",
        "Providence",
        "Quartier Militaire",
        "QUATRE BORNES",
        "Quatre Cocos",
        "Quatre Soeurs",
        "Queen Victoria",
        "Reduit",
        "Richelieu",
        "Ripailles",
        "Riviere des Anguilles",
        "Riviere des Creoles",
        "Riviere du Poste",
        "Riviere du Rempart",
        "Roche Terre",
        "Roches Noires",
        "Rodrigues - Grand Montagne",
        "Rodrigues - La Ferme",
        "Rodrigues - Mont Lubin",
        "Rodrigues - Port Mathurin",
        "Rodrigues - Riviere Coco",
        "Rose Belle",
        "Rose- Hill",
        "Saint Julien d'Hotman",
        "Saint Julien Village",
        "Saint Pierre",
        "Sebastopol",
        "Souillac",
        "St Hubert",
        "Surinam",
        "Tamarin",
        "Terre Rouge",
        "The Vale",
        "Tombeau Bay",
        "Triolet",
        "Trois Boutiques",
        "Trou aux Biches",
        "Trou D'Eau Douce",
        "Tyack",
        "Union Ducray / St Aubin",
        "Union Park",
        "Vacoas / Floreal",
        "Verdun",
        "Ville Bague",
      ],
      driverOptions: [],
      selectedDataItems: [],
      currentDate: new Date().toISOString().split("T")[0],
    };
  },
  computed: {
    ...mapGetters("distributionModule", {
      vehicleOptions: "vehicles",
      loading: "loading",
    }),

    ...mapGetters("userModule", {
      driverList: "users",
      loading: "loading",
    }),

    ...mapGetters("productModule", {
      products: "product",
      loading: "loading",
    }),

    // ...mapGetters("invoiceModule", {
    //   deliveryItems: "invoices",
    //   invoiceId: "invoiceId",
    //   loading: "loading",
    // }),
    ...mapGetters("deliveryModule", {
      deliveryItems: "deliverynotes",
      loadingnotes: "loading",
    }),

    ...mapGetters("salesPersonModule", {
      SalesPersonList: "salesperson",
      loading: "loading",
    }),

    ...mapGetters("deliveriesModule", {
      // selectedItems: "item",
      loading: "loading",
    }),

    formattedDeliveryItems() {
      if (!this.deliveryItems || !Array.isArray(this.deliveryItems)) {
        return [];
      }
      return this.deliveryItems.flatMap((invoice) => {
        if (!invoice.invoiceItem && invoice.status !== "PENDING") {
          return [];
        }
        return invoice.invoiceItem.map((item) => ({
          ...item,
          invoiceNo: invoice.invoiceNo,
          //selected: false
        }));
      });
    },
    // selectedItems() {
    //    this.formattedDeliveryItems.filter(item => item.selected);
    // },
    selectedItems() {
      //console.log("selecteddata", this.deliveryItems.filter((item) => item.selected));
      return this.deliveryItems.filter((item) => item.selected);
    },
    filteredDeliveryItems() {
      return this.deliveryItems
        .map((item) => {
          const customer =
            item.invoice.leadClient.type === 1
              ? item.invoice.leadClient.companyName
              : `${item.invoice.leadClient.surname || ""} ${
                  item.invoice.leadClient.otherName || ""
                }`.trim();

          const address = `${item.invoice.leadClient.addressLine1 || ""} ${
            item.invoice.leadClient.addressLine2 || ""
          }`.trim();

          return {
            ...item,
            invoiceNo: item.invoice.invoiceNo,
            customer, // Precomputed for sorting
            address, // Precomputed for sorting
          };
        })
        .filter((item) => {
          const matchesInvoiceNo =
            !this.filters.invoiceNo ||
            item.invoiceNo
              .toLowerCase()
              .includes(this.filters.invoiceNo.toLowerCase());
          const matchesCustomer =
            !this.filters.customer ||
            item.customer
              .toLowerCase()
              .includes(this.filters.customer.toLowerCase());
          const matchesItemId =
            !this.filters.itemId ||
            this.getProductName(item.itemId)
              .toLowerCase()
              .includes(this.filters.itemId.toLowerCase());
          const matchesAddress =
            !this.filters.address ||
            item.address
              .toLowerCase()
              .includes(this.filters.address.toLowerCase());
          const matchesStatus =
            this.filters.status === null || item.status === this.filters.status;
          const matchesDescription =
            !this.filters.note ||
            (item.note || "")
              .toLowerCase()
              .includes(this.filters.note.toLowerCase());
          const matchesQuantity =
            !this.filters.quantity ||
            parseFloat(item.quantity) === parseFloat(this.filters.quantity);

          return (
            matchesInvoiceNo &&
            matchesCustomer &&
            matchesItemId &&
            matchesAddress &&
            matchesStatus &&
            matchesDescription &&
            matchesQuantity
          );
        });
    },
  },

  methods: {
    clearFilters() {
      // Reset the filters object
      this.filters = {
        invoiceNo: "",
        customer: "",
        itemId: "",
        note: "",
        address: "",
        status: null,
        quantity: "",
      };
      this.$forceUpdate(); // Triggers a reactive update in Vue if necessary
    },
    ...mapActions("distributionModule", ["getVehicleListAction"]),
    ...mapActions("userModule", ["getUserListAction"]),
    ...mapActions("deliveriesModule", [
      "addDeliveryAction",
      "getDeliveryByIDAction",
      "getDeliveryListAction",
      "getDeliveryItemListAction",
      "addDeliveryItemAction",
      "updateDeliveryItemStatusAction",
    ]),

    ...mapActions("productModule", ["getProductListAction"]),
    //...mapActions("invoiceModule", ["getInvoicesListAction", "getInvoiceIdAction"]),
    ...mapActions("salesPersonModule", ["getSalesPersonListAction"]),
    ...mapActions("deliveryModule", [
      "getAllDeliveryNoteListAction",
      "updateDeliveryNotesAction",
    ]),
    additionalFieldChanges(customFields) {
      this.delivery.additionalFields = customFields;
    },
    getProductName(id) {
      const prod = this.products.find((s) => s.id === id);
      return prod ? prod.name : "-";
    },

    logSelectedRow(item) {
       console.log(item);
      // console.log("itemsss", item.selected);
      if (item.selected) {
        if (!this.selectedDataItems.includes(item)) {
          item.status = "SCHEDULED";
          this.selectedDataItems.push(item);
        }
      } else {
        const index = this.selectedDataItems.indexOf(item);
        if (index > -1) {
          this.selectedDataItems.splice(index, 1);
        }
      }
    },

    reloadTable() {
      this.reloadFlag;
    },

    successAdd() {
      this.$swal({
        title: "Delivery Added!",
        text: "You have successfully added a Delivery!",
        icon: "success",
        customClass: {
          confirmButton: "btn btn-primary",
        },
        buttonsStyling: false,
      });
      // .then(() => {
      //   // Reload the page after the user clicks the confirm button

      //   this.$router.push({ path: "/delivery/DeliveryManagement/deliveries" }).then(() => {
      //     // After navigation, trigger the table to reload data
      //     this.$emit("reloadTableData");
      //   });

      // });
    },
    // error
    errorAdd() {
      this.$swal({
        title: "Error!",
        text: " Failed!",
        icon: "error",
        customClass: {
          confirmButton: "btn btn-primary",
        },
        buttonsStyling: false,
      });
    },

    errorItemAdd() {
      this.$swal({
        title: "Error!",
        text: " Please select a delivery item!",
        icon: "error",
        customClass: {
          confirmButton: "btn btn-primary",
        },
        buttonsStyling: false,
      });
    },

    // errorItemQuantity() {
    //   this.$swal({
    //     title: "Error!",
    //     text: " Please insert a quantity!",
    //     icon: "error",
    //     customClass: {
    //       confirmButton: "btn btn-primary",
    //     },
    //     buttonsStyling: false,
    //   });
    // },

    successNoteAdd() {
      this.$swal({
        title: "Delivery Added!",
        text: "You have successfully added a Delivery Item!",
        icon: "success",
        customClass: {
          confirmButton: "btn btn-primary",
        },
        buttonsStyling: false,
      });
    },

    async SubmitDelivery() {
      const isValid = await this.$refs.AddDeliveryForm.validate();
      if (isValid) {
        if (this.selectedDataItems.length == 0) {
          this.errorItemAdd();
          return;
        }

        //  if (this.selectedItems.quantity === null || this.selectedItems.quantity === 0){
        //   this.errorItemQuantity();
        //   return;
        //  }

        let loader = this.$swal({
          title: "Processing...",
          text: "Please wait while the Delivery is being added",
          onOpen: () => {
            this.$swal.showLoading();
          },
          allowOutsideClick: () => !this.$swal.isLoading(),
          buttonsStyling: false,
          showConfirmButton: false,
        });

        //const isValid = this.$refs.AddDeliveryForm.validate();
        if (isValid) {
          console.log("errrr222", isValid);
          let payload = {
            vehicleID: this.delivery.vehicleId,
            driverID: this.delivery.driverId,
            pickUpLocation: this.delivery.pickUpLocation,
            dropOffLocation: this.delivery.dropOffLocation,
            deliveryDate: this.delivery.deliveryDate,
            deliveryStatus: this.delivery.deliveryStatus,
            note: this.delivery.notes,
            additionalFields: this.delivery.additionalFields,
          };

          this.addDeliveryAction(payload)
            .then(() => {
              this.successAdd();

              //const selectedit = this.deliveryItems.filter(item => item.selected);
              const selectedit = this.selectedDataItems;

              console.log("edit", selectedit);
              const itemDetailPromises = selectedit.map((item) => {
                let payload2 = {
                  deliveryID: payload.id,
                  deliveryNoteID: item.id,
                  productID: item.itemId,
                  quantity: item.quantity,
                  note:item.note,
                  status: "SCHEDULED",
                  specialInstructions: "",
                };
                console.log("payload2", payload2);
                return this.addDeliveryItemAction(payload2).then(
                  () => payload2
                );
              });
              console.log("edit2");
              return Promise.all(itemDetailPromises);
            })
            .then((itemDetails) => {
              const itemNotePromises = itemDetails.map((detail) => {
                let payload3 = {
                  id: detail.deliveryNoteID, // Delivery note ID for this item
                  itemId: detail.productID, // Item ID for this item
                  invoiceItemId: detail.id, // Invoice item ID
                  quantity: detail.quantity, // Quantity for this item
                  deliveryDate: this.delivery.deliveryDate, // Use the provided delivery date
                  note: detail.note, // Notes for the delivery
                  status: 5, // Status (use the appropriate value)
                };
                console.log("payload3", payload3);

                // Call updateDeliveryNotesAction for each record
                return this.updateDeliveryNotesAction(payload3);
              });

              //   let payload3 = {
              //     id: itemDetails[0].deliveryNoteID,
              //     itemId: itemDetails[0].productID,
              //     invoiceItemId: itemDetails[0].id,
              //     quantity: itemDetails[0].quantity,
              //     deliveryDate: this.delivery.deliveryDate,
              //     note: this.delivery.notes,
              //     status: 5
              //   };
              //  // console.log("payload3", payload3);
              //   return this.updateDeliveryNotesAction(payload3);
              // });
              return Promise.all(itemNotePromises);
            })
            .then(() => {
              this.successNoteAdd();
              this.$router.push({ name: "deliveries" });
            })
            .catch((error) => {
              //this.errorAdd();
              this.clearDeliveryForm();
              //this.error(ex.response.data);
               console.log("errorrrrrrr");
            });
        }
      }
    },

    clearDeliveryForm() {
      this.delivery.vehicleId = "";
      this.delivery.driverId = "";
      this.delivery.pickUpLocation = "";
      this.delivery.dropOffLocation = "";
      this.delivery.deliveryDate = "";
      this.delivery.deliveryStatus = "";
      this.delivery.notes = "";
      this.delivery.additionalFields = "";

      this.selectedItems.forEach((item) => {
        item.selected = false;
      });
    },

    // getProductName(id) {
    //   for (let i = 0; i < this.products.length; i++) {
    //     if (this.products[i].id == id) {
    //       return this.products[i].name;
    //     }
    //   }
    // },

    toggleModal() {
      this.modalOpen = !this.modalOpen;
    },
    saveChanges() {
      // Save changes and close the modal
      console.log(
        "selectdel",
        this.deliveryItems.filter((item) => item.selected === true)
      );
      const selected = this.deliveryItems.filter((item) => item.selected);
      console.log("Selected items:", selected);

      //this.selectedItems = selected;
      //this.$store.dispatch('addDeliveryItemAction', selected);
      this.modalOpen = false;
    },
    editField(item, field) {
      this.$set(item, field, true);
    },
    saveField(item, field) {
      this.$set(item, field, false);
      // Additional logic to save the changes, e.g., API call
    },
    getStatusColor(status) {
      switch (status) {
        case "PENDING":
          return "warning";
        case "INTRANSIT":
          return "info";
        case "DELIVERED":
          return "success";
        default:
          return "secondary";
      }
    },
  },
  async mounted() {
    //this.clearDeliveryForm();
    this.selectedItems.forEach((item) => {
      item.selected = false;
    });
    await this.getVehicleListAction();
    await this.getUserListAction().then(() => {
        this.driverList.map((v, i) =>
          this.driverOptions.push({
            value: v.id,
            text: `${v.firstName} ${v.lastName || ''}`,
            item: v,
          })
        );
      });

    await this.getAllDeliveryNoteListAction();
    //await this.getInvoiceIdAction(this.invoiceId);

    await this.getProductListAction(true);
    await this.getDeliveryListAction();
    // await this.getDeliveryItemListAction();
    // try {
    //   const response = await this.getInvoicesListAction();
    //   if (response.success) {
    //     this.totalRows = this.deliveryItems.length;
    //   } else {
    //     console.error("Failed to fetch delivery notes", response.message);
    //   }
    // } catch (error) {
    //   console.error(error);
    // }

    // try {
    //   const invoiceIdResponse = await this.getInvoiceIdAction(this.invoiceId);
    //   if (invoiceIdResponse.success) {
    //     this.deliveryItems = invoiceIdResponse.result;
    //     this.deliveryItems.forEach(invoice => {
    //       invoice.invoiceItem.forEach(element => {
    //         this.products.push({
    //           value: element.itemId,
    //           text: element.itemName,
    //         });
    //       });
    //     });
    //   } else {
    //     console.error("Failed to fetch invoice details", invoiceIdResponse.message);
    //   }
    // } catch (error) {
    //   console.error(error);
    // }
    this.loading = false;
  },
};
</script>

<style scoped>
.modal-content {
  width: auto;
}
</style>
